/**
 * Profile service — profile CRUD + avatar management.
 * Class-based singleton per architecture rules.
 */
import { supabase } from '../supabase'
import type { UpdateRow } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { mapProfile } from './profile.utils'
import type { UserProfile } from '../types/auth.types'
import type { UpdateProfileInput } from '../validations/profile.schema'
import { generateBlurhash } from '../utils/blurhash'
import { assertObjectStored, compressImage } from '../utils/image-compress'
import { uploadBytesWithSessionRecovery } from '../utils/storage-verify'
import { requireLiveUserId } from './session-identity'

class ProfileServiceImpl {
  async getProfile(userId: string): Promise<UserProfile> {
    // Own row, via the definer RPC. `profiles` no longer grants column-level
    // SELECT beyond the public subset (display name, avatar, ratings), so
    // `select('*')` — and any select naming a private column — now returns 42501
    // for the caller's own row as much as for anyone else's. A grant cannot make
    // an exception for your own row; current_profile() is that exception, and it
    // resolves the subject from auth.uid() rather than an argument.
    void userId
    const { data, error } = await supabase.rpc('current_profile')

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) throw ServiceError.notFound('Profile')

    return mapProfile(data)
  }

  async updateProfile(userId: string, input: UpdateProfileInput): Promise<UserProfile> {
    // Anchor the RLS-checked write to the LIVE session identity, never a
    // hook-cached user id. A cached user.id that has drifted from the current
    // session makes `.eq('id', …)` target the wrong row and Postgres rejects
    // the UPDATE with "new row violates row-level security policy" — the same
    // class the avatar upload hit. getUser() revalidates + refreshes first.
    const liveId = await requireLiveUserId()

    // Map camelCase input → snake_case columns
    const updates: UpdateRow<'profiles'> = {}
    if (input.firstName !== undefined) updates.first_name = input.firstName
    if (input.lastName !== undefined) updates.last_name = input.lastName
    if (input.dateOfBirth !== undefined) updates.date_of_birth = input.dateOfBirth
    if (input.preferredLocale !== undefined) updates.preferred_locale = input.preferredLocale
    if (input.preferredCurrency !== undefined) updates.preferred_currency = input.preferredCurrency
    if (input.cityId !== undefined) updates.city_id = input.cityId
    if (input.countryId !== undefined) updates.country_id = input.countryId

    // Recompute display_name if name fields changed. Wrap the pre-fetch
    // in try/catch — the recompute is a nice-to-have side effect, not a
    // save gate. If a transient RLS / network error breaks the SELECT,
    // we don't want the entire profile update to fail with the masked
    // "no se pudo actualizar el perfil" toast. Fall back to composing
    // display_name from the input alone (only safe when both halves are
    // present); otherwise leave display_name untouched.
    if (input.firstName !== undefined || input.lastName !== undefined) {
      try {
        const current = await this.getProfile(userId)
        const first = input.firstName ?? current.firstName ?? ''
        const last = input.lastName ?? current.lastName ?? ''
        updates.display_name = `${first} ${last}`.trim() || current.displayName
      } catch (err) {
        if (input.firstName && input.lastName) {
          updates.display_name = `${input.firstName} ${input.lastName}`.trim()
        }
        if (__DEV__) console.warn('[profile.updateProfile] display_name pre-fetch failed:', err)
      }
    }

    // Keep the update and read-after-write as distinct operations so errors
    // identify the failing database step instead of hiding behind a chained
    // update/select response.
    const { error: updateError } = await supabase.from('profiles').update(updates).eq('id', liveId)

    if (updateError) throw ServiceError.fromSupabaseError(updateError)

    // Refetch via the canonical reader. If RLS hides the row from the
    // post-update SELECT (very rare, would mean profiles_select_own
    // somehow doesn't permit the user's own row), we'll get a clean
    // PGRST116 with a meaningful message instead of an opaque "no row"
    // null branch. The optimistic mutation in the hook layer settles
    // the cache from this return value.
    return this.getProfile(userId)
  }

  async uploadAvatar(imageUri: string): Promise<string> {
    // Anchor the storage path AND the profile row to the LIVE session identity,
    // never a hook-cached user id. Storage RLS requires the object's top folder
    // to equal auth.uid(); a cached user.id that has drifted from the current
    // session (account switching, a token refresh) is exactly what produced
    // "new row violates row-level security policy" — the path said one uid while
    // the token carried another. getUser() revalidates + refreshes the session,
    // and we write under that exact identity so RLS can never mismatch.
    const { data: authData, error: authErr } = await supabase.auth.getUser()
    const authUid = authData?.user?.id
    if (authErr || !authUid) {
      throw new ServiceError({
        message: `avatar: no authenticated session (${authErr?.message ?? 'no user'})`,
        code: 'AVATAR_NO_SESSION',
        statusCode: 401,
        userMessageKey: 'profile.avatar_session_expired',
        cause: authErr,
      })
    }

    // Each step throws a ServiceError tagged with WHICH step failed. The
    // `message` is engineer-facing (goes to Sentry via captureError at the call
    // site); the `userMessageKey` is the clean, infra-free string the user sees.
    // This path was the only image upload with no error surfacing at all — a
    // generic toast hid whether the read, the storage upload, or the DB write
    // was failing.
    let bytes: Uint8Array
    let blurSource = imageUri
    try {
      const compressed = await compressImage(imageUri, 'avatar', { square: 384 })
      bytes = compressed.bytes
      blurSource = compressed.uri
    } catch (readErr) {
      throw new ServiceError({
        message: `avatar compress failed for ${imageUri}: ${
          readErr instanceof Error ? readErr.message : String(readErr)
        }`,
        code: 'AVATAR_READ_FAILED',
        statusCode: 400,
        userMessageKey: 'profile.avatar_read_failed',
        cause: readErr,
      })
    }
    if (!bytes || bytes.length === 0) {
      throw new ServiceError({
        message: `avatar compress returned empty for ${imageUri}`,
        code: 'AVATAR_EMPTY',
        statusCode: 400,
        userMessageKey: 'profile.avatar_read_failed',
      })
    }

    const filePath = `${authUid}/avatar.jpg`

    const { error: uploadError } = await uploadBytesWithSessionRecovery(
      'avatars',
      filePath,
      bytes,
      {
        upsert: true,
        contentType: 'image/jpeg',
      },
    )

    if (uploadError) {
      throw new ServiceError({
        message: `avatar storage upload failed (${filePath}, ${bytes.length} bytes): ${uploadError.message}`,
        code: 'AVATAR_STORAGE_FAILED',
        statusCode: 400,
        userMessageKey: 'profile.avatar_storage_failed',
        cause: uploadError,
      })
    }

    await assertObjectStored('avatars', filePath)

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath)
    const avatarUrl = `${urlData.publicUrl}?v=${Date.now()}`

    const blurhash = await generateBlurhash(blurSource)

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: avatarUrl, avatar_blurhash: blurhash })
      .eq('id', authUid)

    if (updateError) {
      throw new ServiceError({
        message: `avatar profiles.update failed (${authUid}): ${updateError.message}`,
        code: 'AVATAR_PROFILE_UPDATE_FAILED',
        statusCode: 400,
        userMessageKey: 'profile.avatar_save_failed',
        cause: updateError,
      })
    }

    return avatarUrl
  }

  async deleteAvatar(): Promise<void> {
    // Same as uploadAvatar: key off the live session identity, not a cached id,
    // so storage RLS and the profile update always target auth.uid().
    const { data: authData, error: authErr } = await supabase.auth.getUser()
    const authUid = authData?.user?.id
    if (authErr || !authUid) throw ServiceError.unauthorized('No authenticated session')

    // Remove all possible avatar formats (legacy webp/jpeg + current jpg)
    const { error: removeError } = await supabase.storage
      .from('avatars')
      .remove([`${authUid}/avatar.jpg`, `${authUid}/avatar.webp`, `${authUid}/avatar.jpeg`])

    if (removeError) throw ServiceError.fromSupabaseError(removeError)

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: null, avatar_blurhash: null })
      .eq('id', authUid)

    if (updateError) throw ServiceError.fromSupabaseError(updateError)
  }
}

export const profileService = new ProfileServiceImpl()
